import { useEffect, useRef, useState } from 'react';
import { parcelTerrainClient, ParcelTerrainClientError } from '../lib/parcel-terrain-client';
import type { ParcelTerrainResult, ParcelTerrainState } from '../lib/parcel-terrain-types';
import { ParcelTerrainEvidence } from './ParcelTerrainEvidence';

interface Props {
  subject: ParcelTerrainResult['subject'];
  onBoundaryChanged?: () => void;
}
interface View {
  state: ParcelTerrainState | null;
  busy: boolean;
  error: ParcelTerrainClientError | null;
}

export function ParcelTerrainAnalysis(props: Props) {
  return <TerrainSelection key={JSON.stringify(props.subject)} {...props} />;
}

function TerrainSelection({ subject, onBoundaryChanged }: Props) {
  const [view, setView] = useState<View>({ state: null, busy: true, error: null });
  const generation = useRef(0);
  const active = useRef(false);
  const polling = useRef<ReturnType<typeof setTimeout>>();
  const deadline = useRef<ReturnType<typeof setTimeout>>();
  function clearTimers() {
    clearTimeout(polling.current);
    clearTimeout(deadline.current);
  }
  function current(token: number) { return active.current && generation.current === token; }
  function failed(error: unknown, token: number) {
    if (!current(token)) return;
    clearTimers();
    setView({ state: null, busy: false, error: error instanceof ParcelTerrainClientError ? error : new ParcelTerrainClientError('Terrain status could not be confirmed. Check saved status before trying again.') });
  }
  function accept(state: ParcelTerrainState, token: number, expected?: ParcelTerrainState) {
    if (!current(token)) return;
    if (expected && (state.request_id !== expected.request_id || state.identity_key !== expected.identity_key)) {
      failed(new ParcelTerrainClientError('The terrain request or source version changed. Check saved status to load the current request.'), token);
      return;
    }
    clearTimers();
    const running = state.status === 'queued' || state.status === 'running';
    if (running) {
      const remaining = Date.parse(state.deadline_at || '') - Date.now();
      if (!Number.isFinite(remaining) || remaining <= 0) {
        failed(new ParcelTerrainClientError('The terrain calculation window ended. Check saved status for its outcome.'), token);
        return;
      }
      deadline.current = setTimeout(() => {
        if (!current(token)) return;
        failed(new ParcelTerrainClientError('The terrain calculation window ended. Check saved status for its outcome.'), token);
        generation.current++;
      }, remaining);
      polling.current = setTimeout(() => {
        if (!current(token)) return;
        parcelTerrainClient.read(subject).then(value => accept(value, token, state)).catch(error => failed(error, token));
      }, Math.min(2000, remaining));
    }
    setView({ state, busy: false, error: null });
  }
  function load(method: 'read' | 'calculate', retry = false) {
    clearTimers();
    const token = ++generation.current;
    setView({ state: null, busy: true, error: null });
    const pending = method === 'read' ? parcelTerrainClient.read(subject) : parcelTerrainClient.calculate(subject, retry);
    pending.then(value => accept(value, token)).catch(error => failed(error, token));
  }
  useEffect(() => {
    active.current = true;
    if (subject.boundary_revision) load('read');
    else setView({ state: null, busy: false, error: null });
    return () => { active.current = false; generation.current++; clearTimers(); };
  }, [subject.parcel_id, subject.boundary_revision]);

  const saved = view.state;
  const running = saved?.status === 'queued' || saved?.status === 'running';
  const numeric = saved?.status === 'ready' || saved?.status === 'partial';
  let message = saved?.reason || undefined;
  if (view.busy) message = 'Checking the terrain request…';
  else if (saved?.status === 'queued') message = 'Terrain calculation queued. This panel will show the result when it is ready.';
  else if (saved?.status === 'running') message = 'Calculating from the selected parcel boundary…';
  else if (saved?.status === 'idle') message = 'Calculate terrain for this parcel using the qualified 1 metre elevation source.';
  else if (saved?.status === 'unavailable') message ||= 'Terrain measurements are unavailable for this parcel.';
  const displayStatus = view.busy || running ? 'calculating' : numeric ? 'complete' : view.error || (saved && saved.status !== 'idle') ? 'failed' : 'idle';
  const reloadBoundary = view.error?.status === 409 || saved?.status === 'superseded';
  const ownerDenied = view.error?.status === 403 || view.error?.status === 401;
  return <>
    <ParcelTerrainEvidence subject={subject} mode="qualified_remote" status={displayStatus}
      result={numeric ? saved.result : null} error={view.error?.message || saved?.reason}
      statusMessage={view.error ? undefined : message}
      onCalculate={saved?.can_calculate && !view.busy ? () => load('calculate') : undefined}
      onRetry={saved?.can_retry && !view.busy ? () => load('calculate', true) : undefined} />
    {reloadBoundary && onBoundaryChanged && <button type="button" className="panel-inline-button" onClick={onBoundaryChanged}>Reload parcel boundary</button>}
    {!reloadBoundary && !ownerDenied && !view.busy && !running && subject.boundary_revision && (view.error || saved?.retry_after) &&
      <div className="panel-note">
        {saved?.retry_after && !saved.can_retry && <p>Retry eligibility can be checked after {new Date(saved.retry_after).toLocaleTimeString()}.</p>}
        <button type="button" className="panel-inline-button" onClick={() => load('read')}>Check saved terrain status</button>
      </div>}
  </>;
}
